import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { ensureGematriaGershayim, digitsToHebrewGematria } from "../lib/hebrewGematria";

/**
 * SiddurPage — a single paginated page of the siddur, styled identically
 * to the Tanakh `BookPage` so users feel they're holding the same book.
 *
 * Visual: same `.book-page-paper` shell + `.book-page-inner` layout,
 * same paper texture, same gold accents. Differences from the Tanakh
 * page are content-only:
 *
 *   • Header shows "סידור · נוסח אשכנז" instead of a chapter ref.
 *   • Body is paragraph-grouped under subsection titles instead of
 *     verse-by-verse.
 *   • No verse numbers, no highlights, no notes diamonds.
 *
 * Receives content already paginated by `SiddurBookReader` — it does
 * not split paragraphs itself.
 */
const SiddurPage = forwardRef(function SiddurPage(
  {
    pageIndex,
    totalPages,
    side = "right",
    titleHe = "סידור",
    titleEn = "SIDDUR",
    sectionTitleHe,
    sectionTitleEn,
    isFirstOfSection,
    skipTachanunBanner, // { reason_he } | null
    subsections = [],
    showInstructions = false,
  },
  ref,
) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  // A page with no subsections is the decorative "trailing blank"
  // appended by the paginator to keep the spread even. Render a
  // minimal ornament centered on the paper.
  const isBlank = subsections.length === 0 && !skipTachanunBanner;

  return (
    <div ref={ref} className="book-page-paper" data-density="hard">
      <div
        className={`book-page-inner page-${side}`}
        style={{ position: "absolute", inset: 0 }}
      >
        {isBlank ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-[hsl(36,60%,40%)] opacity-50 text-3xl">✦</div>
          </div>
        ) : (
          <>
            <div className="book-page-header">
              <span className="book-header-title">{isEn ? "Siddur" : titleHe}</span>
              <span className="book-header-num">
                {isEn ? titleHe : titleEn} · {pageIndex + 1}/{totalPages}
              </span>
            </div>

            {isFirstOfSection && sectionTitleHe && (
              <div className="book-chapter-title" data-testid={`siddur-page-section-${pageIndex}`}>
                {/* Prayer section names are bilingual by design: the Hebrew
                    name is the primary heading; in EN mode we pair it with
                    the Latin transliteration above as the eyebrow so the
                    English reader has phonetic recognition without losing
                    the canonical Hebrew title. */}
                {isEn && sectionTitleEn && (
                  <p className="font-latin italic text-[10px] tracking-widest uppercase text-gold/80 mb-1 text-center">
                    {sectionTitleEn}
                  </p>
                )}
                <h3 className="he">{sectionTitleHe}</h3>
                <div className="book-chapter-ornament">✦</div>
              </div>
            )}

            {skipTachanunBanner && (
              <div
                className="mx-auto my-3 max-w-[90%] rounded-sm border-r-2 border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
                dir="rtl"
                data-testid={`siddur-page-tachanun-banner-${pageIndex}`}
              >
                <strong className="font-heading">
                  {isEn ? "Tachanun is omitted today" : "אין תחנון היום"}
                </strong>
                {skipTachanunBanner.reason_he && (
                  <> — {skipTachanunBanner.reason_he}</>
                )}
              </div>
            )}

            <div
              className="book-printed-verses siddur-book-body"
              dir="rtl"
              data-testid={`siddur-page-body-${pageIndex}`}
            >
              {subsections.map((ss, idx) => (
                <Subsection key={`${ss.id}-${idx}`} ss={ss} showInstructions={showInstructions} />
              ))}
            </div>

            <div className="book-page-footer">
              <span className="book-page-folio">{pageIndex + 1}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
});


function Subsection({ ss, showInstructions }) {
  // Stop pointer events on the subsection body — without this the
  // flipbook gesture engine sometimes interprets a finger-drag on the
  // text as a swipe-to-flip. Same trick the Tanakh page uses on
  // verses. Text selection still works because we only block down
  // events on the wrapper, not the inner spans (and `disableFlipByClick`
  // on the flipbook already prevents accidental taps from flipping).
  const stop = (e) => e.stopPropagation();
  return (
    <div
      className={`siddur-book-sub ${ss.dimmed ? "opacity-60" : ""}`}
      data-ref={ss.ref || ""}
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={stop}
    >
      {(ss.title_he || ss.title_en) && (
        <div className="siddur-book-sub-head">
          {ss.title_he && (
            <h4 className="siddur-book-sub-title-he">{ensureGematriaGershayim(ss.title_he)}</h4>
          )}
          {ss.title_en && (
            <span className="siddur-book-sub-title-en">{digitsToHebrewGematria(ss.title_en)}</span>
          )}
        </div>
      )}
      <div className="siddur-book-paragraphs">
        {(ss.paragraphs || []).map((p, i) => (
          <Paragraph key={i} html={p} showInstructions={showInstructions} />
        ))}
      </div>
    </div>
  );
}


function Paragraph({ html, showInstructions }) {
  const tokens = useMemo(() => parseSefariaHtml(html), [html]);
  const instructionTokens = tokens.filter((t) => t.kind === "small");
  const textTokens = tokens.filter((t) => t.kind !== "small");
  return (
    <div className="siddur-book-para">
      {showInstructions && instructionTokens.length > 0 && (
        <p className="siddur-book-instr" dir="rtl">
          {instructionTokens.map((t, i) => (
            <span key={i}>{t.text}</span>
          ))}
        </p>
      )}
      {textTokens.length > 0 && (
        <p className="siddur-book-line" dir="rtl">
          {textTokens.map((t, i) =>
            t.kind === "bold" ? (
              <strong key={i} className="siddur-book-bold">{t.text}</strong>
            ) : (
              <span key={i}>{t.text}</span>
            ),
          )}
        </p>
      )}
    </div>
  );
}


/**
 * Whitelist-aware HTML splitter. Same as in `pages/Siddur.jsx` — kept
 * local here to avoid coupling the book renderer to the scroll
 * renderer (changing one should never break the other).
 *
 * Returns `[{ kind: 'text'|'bold'|'small', text }]`. Tags other than
 * `<b>` / `<small>` are stripped; their inner text is preserved.
 */
function parseSefariaHtml(input) {
  if (!input) return [];
  const re = /<(b|small)>([\s\S]*?)<\/\1>/gi;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) {
      out.push({ kind: "text", text: stripUnknownTags(input.slice(last, m.index)) });
    }
    out.push({
      kind: m[1].toLowerCase() === "b" ? "bold" : "small",
      text: stripUnknownTags(m[2]),
    });
    last = m.index + m[0].length;
  }
  if (last < input.length) {
    out.push({ kind: "text", text: stripUnknownTags(input.slice(last)) });
  }
  return out.filter((t) => (t.text || "").trim().length > 0);
}

function stripUnknownTags(s) {
  return s.replace(/<[^>]+>/g, "");
}


export default SiddurPage;
